
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getBoardingUrl, getPostVerificationRedirectPath } from "@/config/preLaunchMode";

const SIGN_OUT_REDIRECT_PATHS = new Set(['/login']);

const getSuccessCtaLabel = (redirectPath: string): string => {
  if (redirectPath === '/login') {
    return 'Go to Login';
  }

  if (redirectPath === '/dashboard') {
    return 'Go to Dashboard';
  }

  return 'Continue';
};

export const EmailConfirmation = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [successRedirectPath, setSuccessRedirectPath] = useState('/dashboard');
  const navigate = useNavigate();

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      const completeSuccess = async () => {
        const redirectPath = getPostVerificationRedirectPath();
        const shouldSignOut = SIGN_OUT_REDIRECT_PATHS.has(redirectPath);

        if (shouldSignOut) {
          await supabase.auth.signOut();
        }

        setSuccessRedirectPath(redirectPath);
        setStatus('success');
        setMessage(
          redirectPath === '/login'
            ? 'Your email has been confirmed successfully. Please sign in to continue.'
            : 'Your email has been confirmed successfully. Redirecting you to your dashboard now.'
        );
        setTimeout(() => navigate(redirectPath), 2000);
      };

      // Check for error in URL (can be in hash or query params)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const queryParams = new URLSearchParams(window.location.search);
      
      const error = hashParams.get('error') || queryParams.get('error');
      const errorDescription = hashParams.get('error_description') || queryParams.get('error_description');
      
      if (error) {
        console.error('Email confirmation error:', error, errorDescription);
        setStatus('error');
        setMessage(errorDescription || 'There was an error confirming your email. Please try again.');
        return;
      }

      // Let Supabase handle the token exchange from the hash fragment
      // This is the proper way - Supabase client automatically detects tokens in the URL hash
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('Session error:', sessionError);
        setStatus('error');
        setMessage('Failed to confirm email. Please try again.');
        return;
      }

      if (session?.user) {
        // Clear the hash from URL for cleaner appearance
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname);
        }

        await completeSuccess();
      } else {
        // No session - check if there are tokens in the hash that need processing
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        
        if (accessToken && refreshToken) {
          // Tokens exist but getSession didn't pick them up - try setSession
          try {
            const { data, error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });

            if (setSessionError) {
              console.error('Set session error:', setSessionError);
              setStatus('error');
              setMessage('Failed to confirm email. The link may have expired. Please try signing up again.');
              return;
            }

            if (data.user) {
              // Clear the hash from URL
              window.history.replaceState(null, '', window.location.pathname);
              await completeSuccess();
              return;
            }
          } catch (err) {
            console.error('Unexpected error setting session:', err);
          }
        }

        setStatus('error');
        setMessage('Invalid confirmation link. Please try registering again or check your email for a new confirmation link.');
      }
    };

    handleEmailConfirmation();
  }, [navigate]);

  const handleContinue = () => {
    navigate(successRedirectPath);
  };

  const handleResendEmail = () => {
    window.location.href = getBoardingUrl();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {status === 'loading' && <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />}
            {status === 'success' && <CheckCircle className="h-12 w-12 text-green-600" />}
            {status === 'error' && <XCircle className="h-12 w-12 text-red-600" />}
          </div>
          <CardTitle className="text-2xl font-bold">
            {status === 'loading' && 'Confirming Email...'}
            {status === 'success' && 'Email Confirmed!'}
            {status === 'error' && 'Confirmation Failed'}
          </CardTitle>
          <CardDescription>
            {status === 'loading' && 'Please wait while we confirm your email address.'}
            {status === 'success' && 'Your account is now ready to use.'}
            {status === 'error' && 'We encountered an issue confirming your email.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {message && (
            <Alert variant={status === 'error' ? 'destructive' : 'default'}>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}
          
          {status === 'success' && (
            <Button onClick={handleContinue} className="w-full">
              {getSuccessCtaLabel(successRedirectPath)}
            </Button>
          )}
          
          {status === 'error' && (
            <Button onClick={handleResendEmail} className="w-full">
              Try Registering Again
            </Button>
          )}
          
          {status === 'loading' && (
            <div className="text-center">
              <p className="text-sm text-gray-600">
                This may take a few moments...
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
